import { useCallback, useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Loader2, ArrowUp, ArrowDown, Save, Undo2, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { okJSON } from '../lib/api'
import { materialCatalogCache, normalizeMaterialCatalog } from '../lib/catalogs'
import {
  TEXT_LANGS, CATALOG_CODE_RE, MATERIAL_TYPE_CODES,
  pickText, cleanTextMap, emptyTextMap, localTextMap,
} from '../lib/i18nText'

const inputCls = 'w-full text-sm border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:bg-slate-50 disabled:text-slate-500 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500 dark:disabled:bg-slate-800/60'
const badCls = ' border-status-down ring-1 ring-status-down/40 dark:border-status-down'
const iconBtn = 'p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-slate-800'
const TYPE_DOT = { ORE: 'bg-brand', WASTE: 'bg-amber-500' }
const LISTS = ['classifications', 'gradings']
const ADD_KEY = { classifications: 'add_classification', gradings: 'add_grading' }
const NO_ERRORS = new Set()

let keySeq = 0
const newKey = () => `k${++keySeq}`

const fullLabels = (m) => Object.fromEntries(TEXT_LANGS.map(l => [l, typeof m?.[l] === 'string' ? m[l] : '']))

function toEntries(arr) {
  return (Array.isArray(arr) ? arr : []).map(e => ({ key: newKey(), code: e.code || '', labels: fullLabels(e.labels), active: e.active !== false }))
}

// Os dois tipos são fixos no protocolo: o editor sempre mostra ORE e WASTE, nessa ordem.
// Tipo ausente na resposta nasce dos rótulos da própria UI e listas vazias.
function toEditable(cat) {
  const types = Array.isArray(cat?.types) ? cat.types : []
  return {
    version: cat?.version ?? null,
    types: MATERIAL_TYPE_CODES.map(code => {
      const found = types.find(x => x.code === code)
      return {
        code,
        labels: fullLabels(found ? found.labels : localTextMap('material_types', code)),
        classifications: toEntries(found?.classifications),
        gradings: toEntries(found?.gradings),
      }
    }),
  }
}

function toPayload(doc) {
  const entries = (list) => list.map(e => ({ code: e.code.trim(), labels: cleanTextMap(e.labels), active: !!e.active }))
  return {
    version: doc.version,
    types: doc.types.map(ty => ({
      code: ty.code,
      labels: cleanTextMap(ty.labels),
      classifications: entries(ty.classifications),
      gradings: entries(ty.gradings),
    })),
  }
}

const missingRequired = (labels) => !labels.pt?.trim() || !labels.en?.trim()

// Espelha as regras do PUT: código no formato, único dentro do tipo e da lista, pt e
// en obrigatórios. A mensagem do backend continua sendo mostrada se ele recusar algo
// que esta checagem deixou passar.
function validate(doc, t, lang) {
  const messages = []
  const bad = new Set()
  doc.types.forEach((ty, ti) => {
    const typeName = pickText(ty.labels, lang, ty.code)
    if (missingRequired(ty.labels)) {
      bad.add(`${ti}:labels`)
      messages.push(t('admin:production.catalog.err_labels', { where: typeName }))
    }
    LISTS.forEach(list => {
      const seen = new Map()
      ty[list].forEach(e => {
        const code = e.code.trim()
        seen.set(code, (seen.get(code) || 0) + 1)
      })
      ty[list].forEach(e => {
        const code = e.code.trim()
        const where = `${typeName} › ${t(`admin:production.catalog.${list}`)} › ${code || t('admin:production.catalog.new_item')}`
        if (!code) {
          bad.add(`${ti}:${e.key}:code`)
          messages.push(t('admin:production.catalog.err_code_empty', { where }))
        } else if (!CATALOG_CODE_RE.test(code)) {
          bad.add(`${ti}:${e.key}:code`)
          messages.push(t('admin:production.catalog.err_code', { where, code }))
        } else if (seen.get(code) > 1) {
          bad.add(`${ti}:${e.key}:code`)
          messages.push(t('admin:production.catalog.err_dup', { where, code }))
        }
        if (missingRequired(e.labels)) {
          bad.add(`${ti}:${e.key}:labels`)
          messages.push(t('admin:production.catalog.err_labels', { where }))
        }
      })
    })
  })
  return { messages: [...new Set(messages)], bad }
}

function LabelInputs({ labels, disabled, invalid, onChange }) {
  return TEXT_LANGS.map(l => (
    <td key={l} className="py-1 pr-2 align-top">
      <input
        value={labels[l]}
        disabled={disabled}
        placeholder={l.toUpperCase()}
        aria-label={l.toUpperCase()}
        onChange={e => onChange({ ...labels, [l]: e.target.value })}
        className={inputCls + (invalid && (l === 'pt' || l === 'en') && !labels[l].trim() ? badCls : '')}
      />
    </td>
  ))
}

function EntryRow({ entry, index, count, disabled, badCode, badLabels, onChange, onMove, onRemove }) {
  const { t } = useTranslation()
  return (
    <tr className="border-b border-slate-100 dark:border-slate-800 last:border-0">
      <td className="py-1 pr-2 align-top w-44">
        <input
          value={entry.code}
          disabled={disabled}
          placeholder={t('admin:production.cols.code')}
          onChange={e => onChange({ ...entry, code: e.target.value.toUpperCase() })}
          className={`${inputCls} font-mono uppercase${badCode ? badCls : ''}`}
        />
      </td>
      <LabelInputs labels={entry.labels} disabled={disabled} invalid={badLabels} onChange={labels => onChange({ ...entry, labels })} />
      <td className="py-1 pr-2 align-middle text-center" title={t('admin:production.catalog.active_hint')}>
        <input type="checkbox" checked={entry.active} disabled={disabled}
          onChange={e => onChange({ ...entry, active: e.target.checked })} className="accent-[#017f96]" />
      </td>
      <td className="py-1 align-middle whitespace-nowrap text-right">
        <button type="button" disabled={disabled || index === 0} onClick={() => onMove(-1)} title={t('admin:production.catalog.move_up')} className={iconBtn}><ArrowUp size={14} /></button>
        <button type="button" disabled={disabled || index === count - 1} onClick={() => onMove(1)} title={t('admin:production.catalog.move_down')} className={iconBtn}><ArrowDown size={14} /></button>
        <button type="button" disabled={disabled} onClick={onRemove} title={t('admin:production.catalog.remove')} className={`${iconBtn} hover:!text-status-down`}><Trash2 size={14} /></button>
      </td>
    </tr>
  )
}

function EntryList({ list, typeIndex, entries, disabled, bad, onChange }) {
  const { t } = useTranslation()
  const update = (i, next) => onChange(entries.map((e, j) => (j === i ? next : e)))
  const move = (i, d) => {
    const j = i + d
    if (j < 0 || j >= entries.length) return
    const next = [...entries]
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }
  const add = () => onChange([...entries, { key: newKey(), code: '', labels: emptyTextMap(), active: true }])
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5">{t(`admin:production.catalog.${list}`)}</p>
      {entries.length === 0 ? (
        <p className="text-xs text-slate-400 dark:text-slate-500 py-1">{t('admin:production.catalog.empty_list')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="text-left text-[11px] font-medium text-slate-400 dark:text-slate-500">
                <th className="pb-1 pr-2 font-medium">{t('admin:production.cols.code')}</th>
                {TEXT_LANGS.map(l => <th key={l} className="pb-1 pr-2 font-medium">{l.toUpperCase()}</th>)}
                <th className="pb-1 pr-2 font-medium text-center">{t('admin:production.catalog.active')}</th>
                <th className="pb-1" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <EntryRow
                  key={e.key}
                  entry={e}
                  index={i}
                  count={entries.length}
                  disabled={disabled}
                  badCode={bad.has(`${typeIndex}:${e.key}:code`)}
                  badLabels={bad.has(`${typeIndex}:${e.key}:labels`)}
                  onChange={next => update(i, next)}
                  onMove={d => move(i, d)}
                  onRemove={() => onChange(entries.filter((_, j) => j !== i))}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button type="button" disabled={disabled} onClick={add}
        className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">
        <Plus size={13} /> {t(`admin:production.catalog.${ADD_KEY[list]}`)}
      </button>
    </div>
  )
}

function TypeCard({ type, typeIndex, disabled, bad, onChange }) {
  const { t, i18n } = useTranslation()
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className={`w-2.5 h-2.5 rounded-full ${TYPE_DOT[type.code] || 'bg-slate-400'}`} />
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{pickText(type.labels, i18n.language, type.code)}</span>
          <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">{type.code}</span>
        </div>
        <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mb-1">{t('admin:production.catalog.type_name')}</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <tbody>
              <tr>
                <LabelInputs labels={type.labels} disabled={disabled} invalid={bad.has(`${typeIndex}:labels`)}
                  onChange={labels => onChange({ ...type, labels })} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {LISTS.map(list => (
        <EntryList key={list} list={list} typeIndex={typeIndex} entries={type[list]} disabled={disabled} bad={bad}
          onChange={entries => onChange({ ...type, [list]: entries })} />
      ))}
    </div>
  )
}

// Catálogo de materiais do VMC 0.8 (tipo, classificação, teor), um documento só para o
// produto: o PUT troca o documento inteiro e devolve a versão nova. Não empurra nada aos
// devices; o VMC recebe no próximo identity, no começo do turno.
export default function AdminMaterialCatalog() {
  const { t, i18n } = useTranslation()
  const { mayEdit, reasonEdit } = useRegistryPerms()
  const [doc, setDoc] = useState(null)
  const [baseline, setBaseline] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [savedVersion, setSavedVersion] = useState(null)
  const [showErrors, setShowErrors] = useState(false)

  const adopt = useCallback((cat) => {
    const ed = toEditable(cat)
    setDoc(ed)
    setBaseline(JSON.stringify(ed))
    setShowErrors(false)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      adopt(await materialCatalogCache.load({ force: true }))
    } catch (err) {
      setLoadError(err.message)
    } finally {
      setLoading(false)
    }
  }, [adopt])

  useEffect(() => { load() }, [load])

  const dirty = doc != null && JSON.stringify(doc) !== baseline
  const { messages, bad } = useMemo(
    () => (doc ? validate(doc, t, i18n.language) : { messages: [], bad: new Set() }),
    [doc, t, i18n.language])
  const disabled = !mayEdit || saving

  const save = async () => {
    setSavedVersion(null)
    setSaveError(null)
    if (messages.length) { setShowErrors(true); return }
    setSaving(true)
    try {
      const saved = await okJSON('/api/material-catalog', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toPayload(doc)),
      })
      if (saved) {
        materialCatalogCache.set(saved)
        adopt(normalizeMaterialCatalog(saved))
        setSavedVersion(saved.version ?? null)
      } else {
        await load()
      }
    } catch (err) {
      setSaveError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const discard = () => {
    setDoc(JSON.parse(baseline))
    setShowErrors(false)
    setSaveError(null)
  }

  const setType = (i, next) => {
    setSavedVersion(null)
    setDoc(d => ({ ...d, types: d.types.map((x, j) => (j === i ? next : x)) }))
  }

  return (
    <section className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">{t('admin:production.catalog.title')}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('admin:production.catalog.subtitle')}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{t('admin:production.catalog.order_hint')}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {dirty && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-status-stop/15 text-status-stop">{t('admin:production.catalog.unsaved')}</span>
          )}
          {doc?.version != null && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 tabular-nums">
              {t('admin:production.catalog.version', { version: doc.version })}
            </span>
          )}
        </div>
      </div>

      {loading && !doc ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 dark:text-slate-500 py-6">
          <Loader2 size={16} className="animate-spin" /> {t('common:states.loading')}
        </div>
      ) : loadError && !doc ? (
        <div className="flex items-center gap-3 text-sm text-status-down py-4 flex-wrap">
          <AlertTriangle size={16} className="shrink-0" />
          <span className="min-w-0">{t('admin:production.catalog.load_error', { error: loadError })}</span>
          <button onClick={load} className="text-xs font-medium underline text-slate-600 dark:text-slate-300">{t('common:actions.retry')}</button>
        </div>
      ) : doc && (
        <>
          <div className="space-y-4">
            {doc.types.map((ty, i) => (
              <TypeCard key={ty.code} type={ty} typeIndex={i} disabled={disabled}
                bad={showErrors ? bad : NO_ERRORS} onChange={next => setType(i, next)} />
            ))}
          </div>

          {showErrors && messages.length > 0 && (
            <div className="mt-4 rounded-lg border border-status-down/30 bg-status-down/5 px-3 py-2 text-xs text-status-down">
              <p className="font-semibold mb-1">{t('admin:production.catalog.errors_title')}</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {messages.map(m => <li key={m}>{m}</li>)}
              </ul>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2 flex-wrap">
            <button onClick={save} disabled={disabled || !dirty} title={!mayEdit ? t(reasonEdit) : undefined}
              className="inline-flex items-center gap-1.5 bg-brand text-white rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-800 dark:disabled:text-slate-500">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t('admin:production.catalog.save')}
            </button>
            <button onClick={discard} disabled={disabled || !dirty}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
              <Undo2 size={15} /> {t('admin:production.catalog.discard')}
            </button>
            {savedVersion != null && !dirty && (
              <span className="inline-flex items-center gap-1 text-xs text-status-op">
                <CheckCircle2 size={14} /> {t('admin:production.catalog.saved', { version: savedVersion })}
              </span>
            )}
            {saveError && <span className="text-xs text-red-600 dark:text-red-400 min-w-0">{saveError}</span>}
          </div>
        </>
      )}
    </section>
  )
}
